import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { s } from './TissStyles';
import { localLabel, statusColors } from './TissConstants';
import { formatDate, formatDateTime } from './TissHelpers';
import { formatarGlosa } from './TissGlosas';
import { StatusBadge, PillFilters, useToast } from './TissShared';
import { EtapaIndicador, baixarXmlGuia, abrirPdfGuia, Paginacao } from './TissFluxo';

const LIMIT = 50;
const td = { ...s.td, padding: '7px 10px', verticalAlign: 'top' };
const th = { ...s.th, padding: '8px 10px' };
const mono = { fontFamily: 'monospace', fontSize: '12px' };
const sub = { fontSize: '11px', color: '#888' };

// Uma caixa de busca só: número curto = protocolo, número longo = carteirinha, texto = paciente.
function interpretarBusca(txt) {
  const t = (txt || '').trim();
  if (!t) return {};
  if (/^\d{1,8}$/.test(t)) return { protocolo: t };
  if (/^\d{9,}$/.test(t)) return { carteirinha: t };
  return { paciente: t };
}

export default function TabGuias({ dataInicio, dataFim, etapaInicial }) {
  const toast = useToast();
  const navigate = useNavigate();

  const [etapa, setEtapa] = useState(etapaInicial || 'a_enviar');
  const [local, setLocal] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [mostrarLis, setMostrarLis] = useState('nao_enviado');
  const [page, setPage] = useState(1);
  const [contagens, setContagens] = useState({});
  const [atendimentos, setAtendimentos] = useState([]);
  const [guias, setGuias] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ocupado, setOcupado] = useState(null);     // `${id}:${acao}` em andamento
  const [selLis, setSelLis] = useState(new Set());
  const [selErro, setSelErro] = useState(new Set());
  const [resultadoLote, setResultadoLote] = useState(null);
  const [showEnviarModal, setShowEnviarModal] = useState(false);
  const [envioData, setEnvioData] = useState({ local: '', protocolo: '' });

  useEffect(() => { if (etapaInicial) setEtapa(etapaInicial); }, [etapaInicial]);
  useEffect(() => { setPage(1); setSelLis(new Set()); setSelErro(new Set()); setResultadoLote(null); },
    [etapa, local, busca, dataInicio, dataFim]);

  const termos = useMemo(() => interpretarBusca(busca), [busca]);
  const temBusca = Object.keys(termos).length > 0;
  // Com busca, procura em todo o histórico (achar o paciente vale mais que o período).
  const periodo = useMemo(() => (temBusca ? {} : {
    ...(dataInicio ? { dataInicio } : {}), ...(dataFim ? { dataFim } : {}),
  }), [temBusca, dataInicio, dataFim]);

  const loadContagens = useCallback(async () => {
    try {
      const p = {}; if (dataInicio) p.dataInicio = dataInicio; if (dataFim) p.dataFim = dataFim;
      const res = await axios.get(`${API_URL}/tiss/guias/contagens`, { params: p });
      setContagens(res.data || {});
    } catch (err) { console.error('Erro contagens:', err); }
  }, [dataInicio, dataFim]);

  const loadLista = useCallback(async () => {
    setLoading(true);
    try {
      if (etapa === 'a_enviar') {
        const params = { limit: 300, ...periodo, ...termos };
        if (local) params.local_id = local;
        const res = await axios.get(`${API_URL}/tiss/atendimentos-unimed`, { params });
        setAtendimentos(res.data || []);
      } else {
        const params = { page, limit: LIMIT, ...periodo };
        if (etapa !== 'todas') params.etapa = etapa;
        if (local) params.local_id = local;
        if (termos.protocolo) params.protocolo = termos.protocolo;
        if (termos.paciente) params.search = termos.paciente;
        if (termos.carteirinha) params.carteirinha = termos.carteirinha;
        const res = await axios.get(`${API_URL}/tiss/guias`, { params });
        setGuias(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Erro ao carregar guias:', err);
      toast.error('Erro ao carregar a lista');
    } finally { setLoading(false); }
  }, [etapa, page, local, periodo, termos]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadContagens(); }, [loadContagens]);
  useEffect(() => { loadLista(); }, [loadLista]);
  const refresh = () => { loadContagens(); loadLista(); };

  // ---------- ações (as mesmas da tela anterior) ----------
  const executar = async (guiaId, acao) => {
    if (acao === 'cancelar' && !window.confirm('Cancelar esta guia?\n\nO cancelamento é enviado à UNIMED e a guia sai do faturamento. Só é possível porque ela ainda não entrou em nenhum lote.')) return;
    if (acao === 'informar-realizado' && !window.confirm('Informar os procedimentos realizados desta guia?\n\nIsso gera o XML de procedimentos executados para o lote.')) return;
    setOcupado(`${guiaId}:${acao}`);
    try {
      const res = await axios.post(`${API_URL}/tiss/guias/${guiaId}/${acao}`);
      if (acao === 'recuperar-autorizacao') {
        if (res.data.encontrada && res.data.guia?.status === 'autorizado') toast.success(res.data.mensagem);
        else toast.info(res.data.mensagem || 'A UNIMED não devolveu autorização para esta guia');
      } else if (acao === 'status') {
        const st = res.data.guia?.status;
        toast.info(`Status: ${statusColors[st]?.label || st || 'sem alteração'}`);
      } else if (acao === 'informar-realizado') toast.success('Procedimentos realizados registrados');
      else if (acao === 'reenviar') toast.info(`Reenvio: ${statusColors[res.data.guia?.status]?.label || 'concluído'}`);
      else if (acao === 'cancelar') toast.success('Guia cancelada');
      refresh();
    } catch (err) {
      const d = err.response?.data;
      if (d?.problemas?.length) toast.error(`${d.total_problemas} exame(s) sem código TUSS válido. Corrija na tabela de preços do convênio UNIMED.`);
      else toast.error(d?.error || `Erro na ação ${acao}`);
    } finally { setOcupado(null); }
  };

  const enviarAtendimento = async (loc, protocolo) => {
    setOcupado(`${loc}-${protocolo}:enviar`);
    try {
      const res = await axios.post(`${API_URL}/tiss/guias/enviar`, { local: loc, protocolo });
      const g = res.data.guia; const lbl = statusColors[g?.status]?.label || g?.status;
      if (res.data.sucesso) toast.success(`Guia enviada: ${lbl}${g?.numero_autorizacao ? ` | Senha ${g.numero_autorizacao}` : ''}`);
      else toast.warning(`${lbl}${g?.ultimo_erro || g?.motivo_negativa ? ': ' + (g.ultimo_erro || g.motivo_negativa) : ''}`);
      refresh();
      return true;
    } catch (err) {
      const d = err.response?.data;
      if (d?.problemas?.length) toast.error(`Não é possível enviar: ${d.total_problemas} exame(s) sem código TUSS válido.`);
      else toast.error(d?.error || 'Erro ao enviar guia');
      return false;
    } finally { setOcupado(null); }
  };

  const enviarSelecionados = async () => {
    const itens = atendimentosVisiveis.filter(at => selLis.has(chave(at)))
      .map(at => ({ local: (at.LOCAL || '').trim(), protocolo: at.PROTOCOLO }));
    if (!itens.length || !window.confirm(`Enviar ${itens.length} guia(s) para a UNIMED?`)) return;
    setOcupado('lote:enviar');
    try {
      const res = await axios.post(`${API_URL}/tiss/guias/enviar-lote`, { itens });
      setResultadoLote({ titulo: 'Envio', ...res.data });
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao enviar'); }
    finally { setOcupado(null); }
  };

  const reenviarSelecionadas = async () => {
    const ids = [...selErro];
    if (!ids.length || !window.confirm(`Reenviar ${ids.length} guia(s)?`)) return;
    setOcupado('lote:reenviar');
    const resultados = [];
    for (const id of ids) {
      try {
        const res = await axios.post(`${API_URL}/tiss/guias/${id}/reenviar`);
        resultados.push({ id, sucesso: true, status: res.data.guia?.status });
      } catch (err) { resultados.push({ id, sucesso: false, erro: err.response?.data?.error || err.message }); }
    }
    setResultadoLote({ titulo: 'Reenvio', total: ids.length, sucesso: resultados.filter(r => r.sucesso).length,
      erros: resultados.filter(r => !r.sucesso).length, resultados });
    setOcupado(null);
  };

  // ---------- A enviar (atendimentos do LIS) ----------
  const chave = (at) => `${(at.LOCAL || '').trim()}-${at.PROTOCOLO}`;
  const atendimentosVisiveis = useMemo(() => (mostrarLis === 'todos' ? atendimentos
    : atendimentos.filter(at => (at.tiss_status || 'nao_enviado') === mostrarLis)), [atendimentos, mostrarLis]);
  const selecionavel = (at) => ['nao_enviado', 'erro'].includes(at.tiss_status || 'nao_enviado');
  const naoEnviados = atendimentos.filter(at => !at.tiss_status || at.tiss_status === 'nao_enviado').length;

  const et = contagens.etapas || {};
  const pills = [
    { id: 'a_enviar', label: 'A enviar (LIS)', count: etapa === 'a_enviar' ? naoEnviados : null, color: '#455a64' },
    { id: 'enviada', label: '2 · Aguardando autorização', count: et.enviada || 0, color: '#1565c0' },
    { id: 'autorizada', label: '3 · Autorizadas', count: et.autorizada || 0, color: '#2e7d32' },
    { id: 'realizada', label: '4 · Realizadas', count: et.realizada || 0, color: '#0d47a1' },
    { id: 'em_lote', label: '5 · Em lote', count: et.em_lote || 0, color: '#6a1b9a' },
    { id: 'lote_enviado', label: '6 · Lote enviado', count: et.lote_enviado || 0, color: '#00695c' },
    { id: 'problema', label: 'Erro / negada', count: et.problema || 0, color: '#c62828' },
    { id: 'cancelada', label: 'Canceladas', count: et.cancelada || 0, color: '#757575' },
    { id: 'todas', label: 'Todas', count: contagens.total || 0, color: '#555' },
  ];

  // ação principal = o próximo passo do fluxo; o resto vai no seletor "Mais"
  const proximoPasso = (g) => {
    if (g.status === 'enviado') return ['status', 'Consultar autorização'];
    if (g.status === 'autorizado') return ['informar-realizado', 'Informar realizado'];
    // "Buscar na UNIMED" fica no menu Mais, não como botão principal: guia em erro que a recepção
    // já passou pelo portal NÃO pode ser recuperada (viraria cobrança em dobro — ver memória
    // tiss-504-conexao-recusada-2026-10-02).
    return null;
  };
  const outrasAcoes = (g) => {
    const a = [];
    if (['autorizado', 'realizado'].includes(g.status) || g.numero_autorizacao) a.push(['pdf', 'Baixar PDF da guia']);
    if (g.tem_xml) a.push(['xml', 'Baixar XML']);
    if (g.status !== 'enviado' && !['cancelado'].includes(g.status)) a.push(['status', 'Consultar status na UNIMED']);
    if (g.status === 'erro') a.push(['recuperar-autorizacao', 'Buscar autorização na UNIMED (antes de reenviar)']);
    if (['erro', 'negado'].includes(g.status)) a.push(['reenviar', 'Reenviar']);
    // Cancelar vale enquanto a guia NÃO entrou em lote (inclusive já realizada).
    if (!g.lote_id && g.status !== 'cancelado') a.push(['cancelar', 'Cancelar guia']);
    const prox = proximoPasso(g);
    return a.filter(([id]) => !prox || id !== prox[0]);
  };
  const acaoMais = (g, id) => {
    if (id === 'pdf') return abrirPdfGuia(g.id);
    if (id === 'xml') return baixarXmlGuia(g.id, toast);
    return executar(g.id, id);
  };
  const verGuia = (g) => navigate(`/tiss-unimed/protocolo/${g.solicitacao_local}/${g.solicitacao_protocolo}`);
  const verAt = (at) => navigate(`/tiss-unimed/protocolo/${(at.LOCAL || '').trim()}/${at.PROTOCOLO}`);

  return (
    <>
      <PillFilters items={pills} active={etapa} onChange={setEtapa} style={{ marginBottom: '10px' }} />

      {/* Filtros em uma linha */}
      <div style={{ ...s.filtersRow, padding: '10px 14px', alignItems: 'center' }}>
        <select style={s.select} value={local} onChange={e => setLocal(e.target.value)}>
          <option value="">Todas as unidades</option>
          <option value="01">Matriz</option>
          <option value="02">Itapecerica</option>
          <option value="04">Pedra do Indaiá</option>
          <option value="CA">Camacho</option>
        </select>
        <input style={{ ...s.input, flex: 1, minWidth: '220px' }} value={buscaInput}
          placeholder="Buscar protocolo, paciente ou carteirinha (Enter)"
          onChange={e => setBuscaInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') setBusca(buscaInput); }} />
        <button style={s.btnPrimary} onClick={() => setBusca(buscaInput)}>Buscar</button>
        {temBusca && <button style={s.btnSecondary} onClick={() => { setBusca(''); setBuscaInput(''); }}>Limpar</button>}
        {etapa === 'a_enviar' && (
          <select style={s.select} value={mostrarLis} onChange={e => setMostrarLis(e.target.value)}>
            <option value="nao_enviado">Só não enviados</option>
            <option value="erro">Só com erro</option>
            <option value="todos">Todos do LIS</option>
          </select>
        )}
        <button style={{ ...s.btnSecondary, marginLeft: 'auto' }} onClick={() => setShowEnviarModal(true)}>Enviar por protocolo</button>
      </div>
      {temBusca && (
        <div style={{ fontSize: '12px', color: '#e65100', margin: '-6px 0 10px 4px' }}>
          Buscando em todo o histórico — o período não se aplica à busca.
        </div>
      )}

      {/* Barra de seleção */}
      {(selLis.size > 0 || selErro.size > 0) && !resultadoLote && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '10px',
          borderRadius: '6px', background: '#e3f2fd', border: '1px solid #90caf9' }}>
          <strong style={{ fontSize: '13px', color: '#1565c0' }}>{selLis.size || selErro.size} selecionada(s)</strong>
          {selLis.size > 0 && <button style={{ ...s.btnPrimary, padding: '5px 14px', fontSize: '12px' }} disabled={!!ocupado} onClick={enviarSelecionados}>
            {ocupado === 'lote:enviar' ? 'Enviando…' : 'Enviar para a UNIMED'}</button>}
          {selErro.size > 0 && <button style={{ ...s.btnPrimary, padding: '5px 14px', fontSize: '12px', background: '#e65100' }} disabled={!!ocupado} onClick={reenviarSelecionadas}>
            {ocupado === 'lote:reenviar' ? 'Reenviando…' : 'Reenviar selecionadas'}</button>}
          <button style={s.btnSecondary} onClick={() => { setSelLis(new Set()); setSelErro(new Set()); }}>Limpar seleção</button>
        </div>
      )}
      {resultadoLote && (
        <div style={{ padding: '10px 14px', marginBottom: '10px', borderRadius: '6px',
          background: resultadoLote.erros ? '#fff8e1' : '#e8f5e9', border: '1px solid ' + (resultadoLote.erros ? '#ffecb3' : '#c8e6c9') }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
            <strong>{resultadoLote.titulo}: {resultadoLote.sucesso} ok, {resultadoLote.erros} com erro (de {resultadoLote.total})</strong>
            <button style={s.btnSmall} onClick={() => { setResultadoLote(null); setSelLis(new Set()); setSelErro(new Set()); refresh(); }}>Fechar</button>
          </div>
          {(resultadoLote.resultados || []).filter(r => !r.sucesso).map((r, i) => (
            <div key={i} style={{ fontSize: '12px', color: '#c62828' }}>{r.local ? `${r.local}/${r.protocolo}` : `Guia #${r.id}`} — {r.erro}</div>
          ))}
        </div>
      )}

      <div style={{ ...s.card, padding: '6px 10px 10px' }}>
        {loading ? <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>Carregando…</div>
        : etapa === 'a_enviar' ? (
          atendimentosVisiveis.length === 0 ? <div style={s.emptyState}><p style={s.emptyText}>Nenhum atendimento UNIMED nesse filtro</p></div> : (
          <table style={s.table}>
            <thead><tr>
              <th style={{ ...th, width: '30px' }}>
                <input type="checkbox" title="Selecionar todos"
                  checked={atendimentosVisiveis.filter(selecionavel).length > 0 && atendimentosVisiveis.filter(selecionavel).every(at => selLis.has(chave(at)))}
                  onChange={e => setSelLis(e.target.checked ? new Set(atendimentosVisiveis.filter(selecionavel).map(chave)) : new Set())} />
              </th>
              <th style={th}>Atendimento</th><th style={th}>Paciente / carteirinha</th><th style={th}>Médico</th>
              <th style={th}>Situação TISS</th><th style={{ ...th, width: '150px' }}></th>
            </tr></thead>
            <tbody>{atendimentosVisiveis.map(at => {
              const k = chave(at); const st = at.tiss_status || 'nao_enviado'; const g = at.tiss_guias?.[0];
              return (
                <tr key={k} style={{ background: selLis.has(k) ? '#f1f8ff' : undefined }}>
                  <td style={td}>{selecionavel(at) && <input type="checkbox" checked={selLis.has(k)}
                    onChange={() => setSelLis(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; })} />}</td>
                  <td style={td}><span style={{ ...mono, fontWeight: 600, color: '#1976d2', cursor: 'pointer' }} onClick={() => verAt(at)}>
                    {(at.LOCAL || '').trim()}/{at.PROTOCOLO}</span><div style={sub}>{localLabel(at.LOCAL)} · {formatDate(at.DATA)}</div></td>
                  <td style={td}>{at.PACIENTE_NOME}
                    <div style={{ ...mono, fontSize: '11px', color: at.alertas?.length ? '#c62828' : '#888', fontWeight: at.alertas?.length ? 700 : 400 }}>
                      {at.CARTEIRINHA || 'sem carteirinha'}{at.alertas?.length ? ` ⚠ ${at.alertas.map(a => a.texto).join('; ')}` : ''}</div></td>
                  <td style={td}><span style={{ fontSize: '12px' }}>{at.MEDICO_NOME}</span>{at.MEDICO_CRM && <div style={sub}>CRM {at.MEDICO_CRM}/{at.MEDICO_UF}</div>}</td>
                  <td style={td}><StatusBadge status={st} />
                    {st === 'erro' && g?.ultimo_erro && <div style={{ ...sub, color: '#c62828', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.ultimo_erro}>{g.ultimo_erro}</div>}</td>
                  <td style={td}>
                    {st === 'nao_enviado' && <button style={{ ...s.btnPrimary, padding: '4px 10px', fontSize: '12px' }} disabled={!!ocupado}
                      onClick={() => enviarAtendimento((at.LOCAL || '').trim(), at.PROTOCOLO)}>{ocupado === `${k}:enviar` ? 'Enviando…' : 'Enviar'}</button>}
                    {st === 'erro' && g && <button style={{ ...s.btnSmall, color: '#2e7d32', borderColor: '#2e7d32' }} disabled={!!ocupado}
                      title="Consulta na UNIMED se a guia já foi autorizada (faça isso antes de reenviar)"
                      onClick={() => executar(g.id, 'recuperar-autorizacao')}>{ocupado === `${g.id}:recuperar-autorizacao` ? 'Buscando…' : 'Buscar na UNIMED'}</button>}
                    {st !== 'nao_enviado' && st !== 'erro' && g && <button style={s.btnSmall} onClick={() => verGuia(g)}>Ver guia</button>}
                  </td>
                </tr>);
            })}</tbody>
          </table>)
        ) : guias.length === 0 ? <div style={s.emptyState}><p style={s.emptyText}>Nenhuma guia nessa etapa e período</p></div> : (
          <>
          <table style={s.table}>
            <thead><tr>
              {etapa === 'problema' && <th style={{ ...th, width: '30px' }}>
                <input type="checkbox" checked={guias.length > 0 && guias.every(g => selErro.has(g.id))}
                  onChange={e => setSelErro(e.target.checked ? new Set(guias.map(g => g.id)) : new Set())} /></th>}
              <th style={th}>Atendimento</th><th style={th}>Paciente / carteirinha</th><th style={th}>Etapa</th>
              <th style={th}>Guia UNIMED / senha</th><th style={th}>Lote</th>
              <th style={th} title="Será preenchido com a leitura do portal SGU-Card">Situação no SGU</th>
              <th style={{ ...th, width: '250px' }}></th>
            </tr></thead>
            <tbody>{guias.map(g => {
              const prox = proximoPasso(g); const mais = outrasAcoes(g);
              const motivo = g.ultimo_erro || (g.motivo_negativa ? formatarGlosa(g.motivo_negativa, { prefixo: 'Glosa ' }) : '');
              return (
                <tr key={g.id} style={{ background: selErro.has(g.id) ? '#fff3e0' : undefined }}>
                  {etapa === 'problema' && <td style={td}><input type="checkbox" checked={selErro.has(g.id)}
                    onChange={() => setSelErro(p => { const n = new Set(p); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })} /></td>}
                  <td style={td}><span style={{ ...mono, fontWeight: 600, color: '#1976d2', cursor: 'pointer' }} onClick={() => verGuia(g)}>
                    {g.solicitacao_local}/{g.solicitacao_protocolo}</span>
                    <div style={sub}>{localLabel(g.solicitacao_local)} · {formatDateTime(g.created_at)}</div></td>
                  <td style={td}>{g.paciente_nome}<div style={{ ...mono, fontSize: '11px', color: '#888' }}>{g.paciente_carteirinha}</div></td>
                  <td style={td}><EtapaIndicador guia={g} />
                    {motivo && g.etapa === 'problema' && <div style={{ ...sub, color: '#c62828', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={motivo}>{motivo}</div>}</td>
                  <td style={td}><span style={mono}>{g.numero_guia_operadora || '—'}</span>{g.numero_autorizacao && <div style={sub}>senha {g.numero_autorizacao}</div>}</td>
                  <td style={td}>{g.numero_lote ? <>
                    <span style={{ ...mono, color: '#1976d2', cursor: 'pointer' }} onClick={() => navigate(`/tiss-unimed/lote/${g.lote_id}`)}>{g.numero_lote}</span>
                    <div style={sub}>{g.lote_protocolo ? `prot. ${g.lote_protocolo}` : 'não enviado'}{g.lote_situacao ? ` · ${g.lote_situacao}` : ''}</div></> : <span style={{ color: '#ccc' }}>—</span>}</td>
                  <td style={{ ...td, color: '#bbb' }}>—</td>
                  <td style={td}><div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <button style={s.btnSmall} onClick={() => verGuia(g)}>Ver</button>
                    {prox && <button style={{ ...s.btnSmall, color: '#0d47a1', borderColor: '#0d47a1' }} disabled={!!ocupado}
                      onClick={() => executar(g.id, prox[0])}>{ocupado === `${g.id}:${prox[0]}` ? 'Aguarde…' : prox[1]}</button>}
                    {mais.length > 0 && <select style={{ ...s.select, padding: '3px 6px', fontSize: '11px', width: 'auto' }} value="" disabled={!!ocupado}
                      onChange={e => { const v = e.target.value; e.target.value = ''; if (v) acaoMais(g, v); }}>
                      <option value="">Mais…</option>
                      {mais.map(([id, lbl]) => <option key={id} value={id}>{lbl}</option>)}
                    </select>}
                  </div></td>
                </tr>);
            })}</tbody>
          </table>
          <Paginacao page={page} limit={LIMIT} total={total} onPage={setPage} />
          </>
        )}
      </div>

      {showEnviarModal && (
        <div style={s.modal} onClick={() => setShowEnviarModal(false)}>
          <div style={{ ...s.modalContent, maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Enviar por protocolo</div>
            <div style={s.row}>
              <div style={s.col}>
                <label style={s.filterLabel}>Unidade</label>
                <select style={{ ...s.select, width: '100%' }} value={envioData.local} onChange={e => setEnvioData({ ...envioData, local: e.target.value })}>
                  <option value="">Selecione</option><option value="01">01 - Matriz</option><option value="02">02 - Itapecerica</option>
                  <option value="04">04 - Pedra do Indaiá</option><option value="CA">CA - Camacho</option>
                </select>
              </div>
              <div style={s.col}>
                <label style={s.filterLabel}>Protocolo</label>
                <input style={s.input} value={envioData.protocolo} onChange={e => setEnvioData({ ...envioData, protocolo: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button style={s.btnSecondary} onClick={() => setShowEnviarModal(false)}>Cancelar</button>
              <button style={s.btnPrimary} disabled={!!ocupado || !envioData.local || !envioData.protocolo}
                onClick={async () => { if (await enviarAtendimento(envioData.local, envioData.protocolo)) { setShowEnviarModal(false); setEnvioData({ local: '', protocolo: '' }); } }}>
                {ocupado ? 'Enviando…' : 'Enviar'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
